// Fuzzy player-name matcher for voice scoring.
// Operator says "next bowler bumrah" / "next batter kohli"; we map the
// spoken token to a player row in the playing XI.
//
// Match tiers (first hit wins):
//   1. Exact short_name or full_name (case-insensitive).
//   2. Substring — player name contains the spoken phrase.
//   3. Last-name token equality (most common in cricket).
//   4. Levenshtein distance on last name, threshold 2 — handles speech-
//      recognition wobbles like "Boomrah" -> "Bumrah", "Kolly" -> "Kohli".

function levenshtein(a, b) {
  if (!a) return b ? b.length : 0;
  if (!b) return a.length;
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + cost,
      );
    }
  }
  return dp[m][n];
}

function norm(s) {
  return (s || "").toString().toLowerCase().trim().replace(/[^a-z0-9 ]/g, "");
}

function lastToken(s) {
  const parts = norm(s).split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : "";
}

// Player rows come in two shapes across the codebase:
//   - Scoring (BBP) :  { short_name, full_name }
//   - Squad / XI   :  { first_name, last_name, nick_name }
//   - Match.add    :  { name, label }
// Normalise to a common candidate shape so matchPlayer doesn't have
// to special-case each caller. Founder report 2026-05-26: voice-coded
// squad/XI/match player names recognised but never selected — root
// cause was matchPlayer reading short_name/full_name on rosters that
// only have first_name/last_name/nick_name.
function _candidateNames(p) {
  if (!p) return { sn: "", fn: "", nick: "" };
  const sn = p.short_name || p.nick_name || p.name || "";
  const fn =
    p.full_name
    || p.name
    || [p.first_name, p.last_name].filter(Boolean).join(" ")
    || "";
  const nick = p.nick_name || "";
  return { sn: norm(sn), fn: norm(fn), nick: norm(nick) };
}

export function matchPlayer(spoken, candidates) {
  if (!spoken || !Array.isArray(candidates) || candidates.length === 0) return null;
  const q = norm(spoken);
  if (!q) return null;

  // 1. Exact match on short_name / full_name / nick_name / first / last
  const exact = candidates.find((p) => {
    const { sn, fn, nick } = _candidateNames(p);
    const first = norm(p.first_name);
    const last = norm(p.last_name);
    return sn === q || fn === q || nick === q || first === q || last === q;
  });
  if (exact) return exact;

  // 2. Substring — any name field contains the spoken phrase
  const sub = candidates.find((p) => {
    const { sn, fn, nick } = _candidateNames(p);
    return (sn && sn.includes(q)) || (fn && fn.includes(q)) || (nick && nick.includes(q));
  });
  if (sub) return sub;

  // 3. Last-name token equality (the cricket scorer's default)
  const last = candidates.find((p) => {
    const { sn, fn } = _candidateNames(p);
    const lastFn = lastToken(fn);
    const lastSn = lastToken(sn);
    const lastF = norm(p.last_name);
    return (lastFn && lastFn === q) || (lastSn && lastSn === q) || (lastF && lastF === q);
  });
  if (last) return last;

  // 4. Fuzzy Levenshtein on last name (threshold 2 — handles
  //    "Boomrah"→Bumrah, "Kolly"→Kohli) across both name shapes.
  let best = null;
  let bestScore = Infinity;
  for (const p of candidates) {
    const { sn, fn } = _candidateNames(p);
    const candidates2 = [
      lastToken(fn),
      lastToken(sn),
      norm(p.last_name),
      norm(p.nick_name),
    ].filter(Boolean);
    for (const cand of candidates2) {
      const dist = levenshtein(q, cand);
      if (dist < bestScore && dist <= 2) {
        bestScore = dist;
        best = p;
      }
    }
  }
  return best;
}
